import { createStyles } from "@mantine/core";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import { updateProfile } from "../../services/profile";
import CreateClaimDrawer from "./CreateClaimDrawer";
import VerifyEligibilityModal from "./VerifyEligibilityModal";
import UsePreviousClaimDrawer from "./UsePreviousClaimDrawer";
import ClaimTypeChooser from "./ClaimTypeChooser";
import { LuPlus, LuFileText } from "react-icons/lu";
import { IoSettingsOutline } from "react-icons/io5";

const useStyles = createStyles(() => ({
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "20px",
    padding: "14px 20px",
    background: "#fff",
    border: "1px solid #E5E5E5",
    borderRadius: "12px",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
  },

  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },

  ctaBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#FFAF4A",
    color: "#171717",
    fontWeight: 600,
    fontSize: "14px",
    fontFamily: "Inter, sans-serif",
    border: "none",
    borderRadius: "8px",
    padding: "9px 16px",
    cursor: "pointer",
    "&:hover": { background: "#F0A03E" },
    "& svg": { width: "16px", height: "16px", flexShrink: 0, display: "block" },
  },

  settingsIconBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    color: "#6B7280",
    border: "none",
    borderRadius: "8px",
    padding: "7px",
    cursor: "pointer",
    "&:hover": { background: "#F3F4F6", color: "#374151" },
    "& svg": { width: "20px", height: "20px", flexShrink: 0, display: "block" },
  },

  startClaimWrap: {
    position: "relative",
    display: "inline-block",
  },

  startClaimMenu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    background: "#fff",
    border: "1px solid #E5E5E5",
    borderRadius: "12px",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.14)",
    padding: "6px",
    minWidth: "280px",
    zIndex: 200,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  startClaimMenuItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "10px 10px",
    borderRadius: "8px",
    cursor: "pointer",
    border: "none",
    background: "transparent",
    width: "100%",
    textAlign: "left",
    "&:hover": { background: "#F9FAFB" },
  },

  startClaimMenuIco: {
    width: "34px",
    height: "34px",
    borderRadius: "8px",
    background: "#FFF4DA",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: "1px",
    "& svg": { width: "16px", height: "16px", color: "#B7791F", display: "block" },
  },

  startClaimMenuInfo: {
    display: "flex",
    flexDirection: "column",
  },

  startClaimMenuTitle: {
    fontFamily: "Inter, sans-serif",
    fontSize: "13.5px",
    fontWeight: 700,
    color: "#171717",
    lineHeight: "1.3",
    margin: "0 0 2px",
    display: "block",
  },

  startClaimMenuSub: {
    fontFamily: "Inter, sans-serif",
    fontSize: "12px",
    fontWeight: 400,
    color: "#667085",
    lineHeight: "1.4",
    margin: 0,
    display: "block",
  },
}));

function ClaimToolbar() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state?.profile);
  const { SubscriptionCard } = useSelector((state) => state?.auth);
  const { billingcard } = useSelector((state) => state?.billing);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [claimDrawerOpen, setClaimDrawerOpen] = useState(false);
  const [eligibilityModalOpen, setEligibilityModalOpen] = useState(false);
  const [prevClaimDrawerOpen, setPrevClaimDrawerOpen] = useState(false);
  const [prefillClaim, setPrefillClaim] = useState(null);
  const dropdownRef = useRef(null);

  React.useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [dropdownOpen]);

  const checkCard = async (action) => {
    if (!SubscriptionCard && billingcard?.BillingPayment === null) {
      await swal({
        title: "Card not Connected",
        text: `Please connect a bank or credit card account to ${action}.`,
        dangerMode: true,
      });
      navigate("/home/billing-settings", { state: { tab: "payment" } });
      return false;
    }
    return true;
  };

  const handleProfessionalClaimClick = async () => {
    // if (!(await checkCard("start a claim"))) return;
    setClaimDrawerOpen(true);
  };

  return (
    <>
      <div className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          <div className={classes.startClaimWrap} ref={dropdownRef}>
            <button
              className={classes.ctaBtn}
              onClick={() => setDropdownOpen((prev) => !prev)}
            >
              <LuPlus />
              Start Claim
            </button>

            {dropdownOpen && (
              <div className={classes.startClaimMenu}>
                <button
                  className={classes.startClaimMenuItem}
                  onClick={() => { setDropdownOpen(false); setChooserOpen(true); }}
                >
                  <div className={classes.startClaimMenuIco}><LuPlus /></div>
                  <div className={classes.startClaimMenuInfo}>
                    <span className={classes.startClaimMenuTitle}>Start a New Claim</span>
                    <span className={classes.startClaimMenuSub}>Pick a claim type and fill it in from scratch.</span>
                  </div>
                </button>

                <button
                  className={classes.startClaimMenuItem}
                  onClick={() => { setDropdownOpen(false); setPrevClaimDrawerOpen(true); }}
                >
                  <div className={classes.startClaimMenuIco}><LuFileText /></div>
                  <div className={classes.startClaimMenuInfo}>
                    <span className={classes.startClaimMenuTitle}>Use a Previous Claim</span>
                    <span className={classes.startClaimMenuSub}>Duplicate a past claim and pre-fill its details.</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>

        <button
          className={classes.settingsIconBtn}
          onClick={() => navigate("/home/billing-settings")}
        >
          <IoSettingsOutline />
        </button>
      </div>

      <ClaimTypeChooser
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onProfessional={handleProfessionalClaimClick}
        onInstitutional={() => {}}
      />

      <CreateClaimDrawer
        opened={claimDrawerOpen}
        onClose={() => { setClaimDrawerOpen(false); setPrefillClaim(null); }}
        prefillClaim={prefillClaim}
      />

      <VerifyEligibilityModal
        opened={eligibilityModalOpen}
        onClose={() => setEligibilityModalOpen(false)}
      />

      <UsePreviousClaimDrawer
        opened={prevClaimDrawerOpen}
        onClose={() => setPrevClaimDrawerOpen(false)}
        onSelect={async (claim) => {
          // if (!(await checkCard("start a claim"))) return;
          setPrefillClaim(claim);
          setClaimDrawerOpen(true);
        }}
      />
    </>
  );
}

export default ClaimToolbar;
